// features/marketing/components/DocumentListTab.tsx
import { FC } from 'react';
import { useMemo, useCallback, useState } from 'react';
import { EditableDataTable } from '@/components/ui/EditableDataTable';
import type { EditableColumn } from '@/components/ui/EditableDataTable';
import Button from '@/components/ui/Button';
import { UploadDocumentTab } from './UploadDocument';
import { Download, Upload } from 'lucide-react';

export type DocumentItem = {
    id: string;
    title: string;
    description: string;
    downloadUrl: string;
    createdAt: string;
};

interface DocumentListTabProps {
    data: DocumentItem[];
    loading?: boolean;
    error?: string | null;
    pagination?: {
        pageIndex: number;
        pageSize: number;
        totalCount: number;
    };
    onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
    onGlobalSearch?: (query: string) => Promise<void>;
    globalFilterValue?: string;

    // 👇 new props for upload modal
    hasLinkedInToken: boolean;
    onDocumentUploaded: (docUrl: string) => void;
    onPostToLinkedIn: (payload: any) => Promise<void> | void;
    onDocumentDeleted: (id: string) => Promise<void> | void;
}

export const DocumentListTab: FC<DocumentListTabProps> = ({
    data,
    loading = false,
    error,
    pagination,
    onPaginationChange,
    onGlobalSearch,
    globalFilterValue,
    hasLinkedInToken,
    onDocumentUploaded,
    onPostToLinkedIn,
    onDocumentDeleted,
}) => {
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    const columns: EditableColumn<DocumentItem>[] = useMemo(
        () => [
            {
                key: 'title',
                header: 'Title',
                editable: false,
                enableSorting: true,
                className: 'w-1/4',
            },
            {
                key: 'description',
                header: 'Description',
                editable: false,
                enableSorting: false,
                className: 'w-1/4',
            },
            {
                key: 'category',
                header: 'Category',
                editable: false,
                enableSorting: false,
                className: 'w-1/4',
            },
        ],
        []
    );


    const handleOpenUploadModal = useCallback(() => {
        setIsUploadModalOpen(true);
    }, []);

    const handleCloseUploadModal = useCallback(() => {
        setIsUploadModalOpen(false);
    }, []);

    const handleDocumentUploadedInternal = useCallback(
        (docUrl: string) => {
            onDocumentUploaded(docUrl);
        },
        [onDocumentUploaded]
    );

    const handlePostToLinkedInInternal = useCallback(
        async (documentId: string) => {
            // Call parent handler (can be sync or async)
            const maybePromise = onPostToLinkedIn?.(documentId);

            if (maybePromise instanceof Promise) {
                await maybePromise;
            }

            // ✅ After LinkedIn post completes, close modal
            setIsUploadModalOpen(false);
        },
        [onPostToLinkedIn]
    );

    return (
        <div className="flex flex-col gap-4">
            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* 🔼 Upload button above table */}
            <div className="flex justify-end">
                <Button onClick={handleOpenUploadModal} className='cursor-pointer flex items-center gap-2'>
                    <Upload className="w-4 h-4" />
                    Upload Document
                </Button>
            </div>

            <EditableDataTable<DocumentItem>
                title="Uploaded Documents"
                columns={columns}
                data={data}
                keyExtractor={(row) => row.id}
                enableEditing={false}
                enableDelete={true}
                onRowDelete={async (row) => { await onDocumentDeleted(row.id); }}
                renderCustomRowActions={(row) => (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                        title="Download Document"
                    >
                        <a
                            href={row.downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center p-0.5"
                        >
                            <Download className="w-4 h-4" />
                        </a>
                    </Button>
                )}
                loading={loading}
                pagination={
                    pagination && {
                        pageIndex: pagination.pageIndex,
                        pageSize: pagination.pageSize,
                        totalCount: pagination.totalCount,
                    }
                }
                onPaginationChange={onPaginationChange}
                onGlobalSearch={onGlobalSearch}
                globalFilterValue={globalFilterValue}
            />

            {/* ⚡ Simple modal implementation */}
            {isUploadModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="w-full max-w-3xl rounded-lg bg-white p-4 shadow-xl">
                        <div className="mb-4 flex items-center justify-between border-b pb-2">
                            <h2 className="text-lg font-semibold">Upload Document</h2>
                            <button
                                type="button"
                                onClick={handleCloseUploadModal}
                                className="text-gray-500 hover:text-gray-700 cursor-pointer"
                            >
                                ✕
                            </button>
                        </div>

                        <UploadDocumentTab
                            hasLinkedInToken={hasLinkedInToken}
                            onDocumentUploaded={handleDocumentUploadedInternal}
                            onPostToLinkedIn={handlePostToLinkedInInternal}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
